import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getTourTheme } from '../theme/themeStyles';

/**
 * A lightweight, dependency-free product tour overlay.
 *
 * Each step targets an element via a CSS selector (typically a `data-tour-id` attribute) and
 * renders:
 *   - 4 semi-transparent backdrop strips around the target (so the target stays visible while
 *     the rest of the screen is dimmed),
 *   - a thin brand-green outline around the target ("spotlight"), and
 *   - a tooltip card anchored to the target with title, description, step counter, and
 *     Skip / Previous / Next (or Finish) buttons.
 *
 * The tour is fully controlled by the parent: pass `active`, `currentStep`, and the step list.
 * Persistence ("have I seen this tour before?") is the parent's responsibility — see
 * `tourStorage` helpers below.
 */

export interface TourStep {
  /** CSS selector for the highlighted element (e.g. `[data-tour-id="sidebar"]`). */
  target: string;
  title: string;
  description: string;
  /** Where to put the tooltip relative to the target. `auto` picks the side with more room. */
  placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right';
  /** Optional hook fired right before this step is shown (e.g. open a panel that hosts the target). */
  beforeStep?: () => void;
}

interface TourGuideProps {
  steps: TourStep[];
  active: boolean;
  currentStep: number;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
  onComplete: () => void;
}

const TOOLTIP_WIDTH = 320;
const TOOLTIP_OFFSET = 12;
const VIEWPORT_MARGIN = 12;
const HIGHLIGHT_PADDING = 6;

/**
 * Storage helpers — the tour is shown once per browser unless the user explicitly re-launches it.
 */
export const tourStorage = {
  key: 'digisaka.tour.seen.v2',
  hasSeen(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(this.key) === '1';
    } catch {
      return false;
    }
  },
  markSeen(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(this.key, '1');
    } catch {
      // ignore quota errors — worst case, prompt shows again
    }
  },
  reset(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(this.key);
    } catch {
      // ignore
    }
  },
};

/** Short tour for BLO users with an empty farm list — how to draw / AI / upload a field. */
export const createFarmTourStorage = {
  key: 'digisaka.tour.create-farm.seen.v1',
  hasSeen(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(this.key) === '1';
    } catch {
      return false;
    }
  },
  markSeen(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(this.key, '1');
    } catch {
      // ignore
    }
  },
  reset(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(this.key);
    } catch {
      // ignore
    }
  },
};

/**
 * Resolve a tour step's selector to the first VISIBLE matching element. This lets us share a
 * single `data-tour-id` between desktop and mobile layouts (e.g. the same `top-search` id is on
 * the lg+ overlay search and on the mobile-header search; only one is rendered at a time, but
 * `display: none` siblings remain in the DOM).
 */
function getRect(selector: string): DOMRect | null {
  if (typeof document === 'undefined') return null;
  const matches = document.querySelectorAll<HTMLElement>(selector);
  for (const el of matches) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return r;
  }
  return null;
}

function pickPlacement(
  rect: DOMRect,
  pref: TourStep['placement'],
  tooltipHeightEstimate: number
): 'top' | 'bottom' | 'left' | 'right' {
  if (pref && pref !== 'auto') return pref;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Prefer below the target; if not enough room, above; then right/left.
  if (vh - rect.bottom >= tooltipHeightEstimate + TOOLTIP_OFFSET + VIEWPORT_MARGIN) return 'bottom';
  if (rect.top >= tooltipHeightEstimate + TOOLTIP_OFFSET + VIEWPORT_MARGIN) return 'top';
  if (vw - rect.right >= TOOLTIP_WIDTH + TOOLTIP_OFFSET + VIEWPORT_MARGIN) return 'right';
  if (rect.left >= TOOLTIP_WIDTH + TOOLTIP_OFFSET + VIEWPORT_MARGIN) return 'left';
  return 'bottom';
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function computeTooltipPosition(
  rect: DOMRect,
  placement: 'top' | 'bottom' | 'left' | 'right',
  tooltipHeight: number
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = 0;
  let left = 0;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + TOOLTIP_OFFSET;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      break;
    case 'top':
      top = rect.top - TOOLTIP_OFFSET - tooltipHeight;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + TOOLTIP_OFFSET;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - TOOLTIP_OFFSET - TOOLTIP_WIDTH;
      break;
  }

  return {
    top: clamp(top, VIEWPORT_MARGIN, vh - tooltipHeight - VIEWPORT_MARGIN),
    left: clamp(left, VIEWPORT_MARGIN, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN),
  };
}

export default function TourGuide({
  steps,
  active,
  currentStep,
  onPrev,
  onNext,
  onSkip,
  onComplete,
}: TourGuideProps) {
  const { isDark } = useTheme();
  const tt = useMemo(() => getTourTheme(isDark), [isDark]);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [tooltipHeight, setTooltipHeight] = useState(180);
  const [missingTarget, setMissingTarget] = useState(false);

  const step = steps[currentStep];
  const isLast = currentStep >= steps.length - 1;

  // Fire `beforeStep` whenever the step changes (e.g. to open a panel that hosts the next target).
  useEffect(() => {
    if (!active || !step) return;
    step.beforeStep?.();
  }, [active, step]);

  // Re-measure the target whenever the step, viewport, or scroll position changes.
  const recompute = useCallback(() => {
    if (!active || !step) return;
    const r = getRect(step.target);
    if (!r) {
      setMissingTarget(true);
      setRect(null);
      return;
    }
    setMissingTarget(false);
    setRect(r);
  }, [active, step]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useEffect(() => {
    if (!active) return;
    // Poll briefly to handle async-rendered targets (e.g. after `beforeStep` mounts something).
    const pollId = window.setInterval(() => {
      recompute();
    }, 250);
    const stopPoll = window.setTimeout(() => window.clearInterval(pollId), 3000);

    const onResize = () => recompute();
    const onScroll = () => recompute();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.clearInterval(pollId);
      window.clearTimeout(stopPoll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [active, recompute]);

  // Allow Esc to skip, ←/→ to navigate.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (isLast) onComplete();
        else onNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentStep > 0) onPrev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, isLast, currentStep, onSkip, onNext, onPrev, onComplete]);

  if (!active || !step) return null;

  // Fallback rendering when the target element is missing: show a centered tooltip with a hint
  // so the tour still progresses instead of getting stuck.
  if (missingTarget || !rect) {
    return (
      <div className="fixed inset-0 z-[300]">
        <div className="absolute inset-0 bg-black/40" onClick={onSkip} aria-hidden />
        <div
          className={tt.tooltipFallback}
          style={{ width: TOOLTIP_WIDTH }}
          role="dialog"
          aria-modal="true"
        >
          <TooltipBody
            tt={tt}
            step={step}
            currentStep={currentStep}
            total={steps.length}
            isLast={isLast}
            onPrev={onPrev}
            onNext={onNext}
            onSkip={onSkip}
            onComplete={onComplete}
            note="(This element isn't on screen right now — moving on.)"
          />
        </div>
      </div>
    );
  }

  const placement = pickPlacement(rect, step.placement, tooltipHeight);
  const tipPos = computeTooltipPosition(rect, placement, tooltipHeight);

  // Highlight box around the target — slightly inflated so the outline doesn't clip the element.
  const hl = {
    top: rect.top - HIGHLIGHT_PADDING,
    left: rect.left - HIGHLIGHT_PADDING,
    width: rect.width + HIGHLIGHT_PADDING * 2,
    height: rect.height + HIGHLIGHT_PADDING * 2,
  };

  return (
    <div className="fixed inset-0 z-[300] pointer-events-none" aria-live="polite">
      {/* Backdrop = 4 strips around the target so the target stays interactive-looking even though
          we don't actually let clicks through (the spotlight border is decorative). The strips
          have `pointer-events-auto` so clicking the dim area dismisses the tour. */}
      <div
        className="absolute bg-black/55 pointer-events-auto"
        style={{ top: 0, left: 0, right: 0, height: Math.max(0, hl.top) }}
        onClick={onSkip}
      />
      <div
        className="absolute bg-black/55 pointer-events-auto"
        style={{
          top: hl.top + hl.height,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        onClick={onSkip}
      />
      <div
        className="absolute bg-black/55 pointer-events-auto"
        style={{
          top: hl.top,
          left: 0,
          width: Math.max(0, hl.left),
          height: hl.height,
        }}
        onClick={onSkip}
      />
      <div
        className="absolute bg-black/55 pointer-events-auto"
        style={{
          top: hl.top,
          left: hl.left + hl.width,
          right: 0,
          height: hl.height,
        }}
        onClick={onSkip}
      />

      {/* Highlight outline around the target (decorative only). */}
      <div
        className={tt.ring}
        style={{ top: hl.top, left: hl.left, width: hl.width, height: hl.height }}
        aria-hidden
      />

      {/* Tooltip card — `pointer-events-auto` so the buttons inside are clickable. */}
      <div
        ref={(el) => {
          if (el) {
            const h = el.getBoundingClientRect().height;
            if (Math.abs(h - tooltipHeight) > 2) setTooltipHeight(h);
          }
        }}
        className={`absolute pointer-events-auto transition-[top,left] duration-200 ${tt.tooltip}`}
        style={{ width: TOOLTIP_WIDTH, top: tipPos.top, left: tipPos.left }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-step-title"
      >
        <TooltipBody
          tt={tt}
          step={step}
          currentStep={currentStep}
          total={steps.length}
          isLast={isLast}
          onPrev={onPrev}
          onNext={onNext}
          onSkip={onSkip}
          onComplete={onComplete}
        />
      </div>
    </div>
  );
}

function TooltipBody({
  tt,
  step,
  currentStep,
  total,
  isLast,
  onPrev,
  onNext,
  onSkip,
  onComplete,
  note,
}: {
  tt: ReturnType<typeof getTourTheme>;
  step: TourStep;
  currentStep: number;
  total: number;
  isLast: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
  onComplete: () => void;
  note?: string;
}) {
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className={tt.stepLabel}>
          Tour · {currentStep + 1} / {total}
        </span>
        <button
          type="button"
          onClick={onSkip}
          className={tt.closeBtn}
          aria-label="Skip tour"
          title="Skip tour"
        >
          <i className="ri-close-line text-base" aria-hidden />
        </button>
      </div>

      <h3 id="tour-step-title" className={tt.title}>
        {step.title}
      </h3>
      <p className={tt.body}>{step.description}</p>
      {note && <p className={tt.note}>{note}</p>}

      <div className="mt-4 flex items-center justify-between">
        <button type="button" onClick={onSkip} className={tt.skipBtn}>
          Skip tour
        </button>
        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <button type="button" onClick={onPrev} className={tt.prevBtn}>
              Previous
            </button>
          )}
          <button
            type="button"
            onClick={() => (isLast ? onComplete() : onNext())}
            className={tt.nextBtn}
          >
            {isLast ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
}
